import {afterNextRender, Component, computed, DestroyRef, ElementRef, inject, input, LOCALE_ID, signal} from '@angular/core';

/** How long a figure takes to count up, and how steeply it settles. */
const DURATION_MS = 1800;

/** Exponential ease-out: fast off the mark, a long soft landing on the final digit. */
function easeOutExpo(t: number): number {
    return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

/**
 * A figure that counts up from zero the first time it scrolls into view.
 *
 * The width never moves. The final figure is always in the box, invisible, and the running
 * figure is painted on top of it out of flow, so the box is as wide as the final
 * figure from the first frame and "9" becoming "10" cannot shift the label beside it. Tabular
 * figures keep the running digits from jittering inside that box.
 *
 * Screen readers get the final figure only, never the ticking one. Reduced motion, and a
 * browser with no IntersectionObserver, get the final figure straight away.
 */
@Component({
    selector: 'cb-count-up',
    template: `
        <span class="final" aria-hidden="true">{{ finalText() }}</span>
        <span class="running" aria-hidden="true">{{ runningText() }}</span>
        <span class="sr-only">{{ finalText() }}</span>
    `,
    styles: [`
        :host {
            display: inline-block;
            position: relative;
            font-variant-numeric: tabular-nums lining-nums;
            font-feature-settings: 'tnum' 1, 'lnum' 1;
        }

        .final, .running {
            white-space: nowrap;
        }

        /* The final figure alone sizes the box. Display faces without tabular figures make
           "1,302" wider than "1,761", so the running figure is laid over it, out of flow. */
        .final {
            visibility: hidden;
        }

        .running {
            position: absolute;
            left: 0;
            top: 0;
        }

        .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            overflow: hidden;
            clip: rect(0 0 0 0);
            white-space: nowrap;
        }
    `],
})
export class CountUpComponent {

    /** The figure to land on, already in its display unit (7.2 for "7.2k+"). */
    readonly value = input.required<number>();
    readonly decimals = input(0);
    /** Appended as is, e.g. "k+" or "+". */
    readonly suffix = input('');
    /** Milliseconds to wait once visible, so a row of figures can land one after another. */
    readonly delay = input(0);

    private readonly format = computed(() => new Intl.NumberFormat(this.locale, {
        minimumFractionDigits: this.decimals(),
        maximumFractionDigits: this.decimals(),
    }));

    readonly finalText = computed(() => this.format().format(this.value()) + this.suffix());

    /** Null until the animation starts: until then the final figure is what shows. */
    private readonly progress = signal<number | null>(null);

    readonly runningText = computed(() => {
        const progress = this.progress();
        if (progress === null) {
            return this.finalText();
        }
        return this.format().format(this.value() * easeOutExpo(progress)) + this.suffix();
    });

    private readonly locale = inject(LOCALE_ID);

    constructor() {
        const host = inject(ElementRef<HTMLElement>).nativeElement as HTMLElement;
        const destroyRef = inject(DestroyRef);

        afterNextRender(() => {
            const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            if (reduced || typeof IntersectionObserver === 'undefined') {
                return;
            }

            let frame = 0;
            let timer = 0;
            this.progress.set(0);

            const observer = new IntersectionObserver(entries => {
                if (!entries.some(entry => entry.isIntersecting)) {
                    return;
                }
                observer.disconnect();
                timer = window.setTimeout(() => {
                    const start = performance.now();
                    const tick = (now: number) => {
                        const t = Math.min(1, (now - start) / DURATION_MS);
                        this.progress.set(t);
                        if (t < 1) {
                            frame = requestAnimationFrame(tick);
                        }
                    };
                    frame = requestAnimationFrame(tick);
                }, this.delay());
            }, {rootMargin: '0px 0px -10% 0px'});

            observer.observe(host);
            destroyRef.onDestroy(() => {
                observer.disconnect();
                clearTimeout(timer);
                cancelAnimationFrame(frame);
            });
        });
    }
}
